import { useState } from "react";
import { Plus, Trash2, X } from "lucide-react";
import type { SegmentRule } from "@/lib/types";
import { BRANDS } from "@/lib/brands";
import { ACTIVITIES, DATE_FIELDS, DATE_OPS, EMAIL_STATUSES, ENGAGEMENT_EVENTS, RULE_TYPES, TEXT_FIELDS, TEXT_OPS } from "@/lib/segments";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { TagPicker } from "../contacts/ContactHeader";
import { useCustomFields, useTags } from "../contacts/hooks";

/** Defaults for a new rule of each type. */
export function newRule(type: SegmentRule["type"]): SegmentRule {
  switch (type) {
    case "tag":
      return { type, tag_ids: [], tag_match: "any" };
    case "tag_event":
      return { type, tag_ids: [], action: "applied" };
    case "brand":
      return { type, brand_ids: [2], marketing_status: "subscribed" };
    case "engagement":
      return { type, event: "email", within_days: 90, has: true };
    case "email_activity":
      return { type, activity: "clicked" };
    case "purchase":
      return { type, has: true };
    case "subscription":
      return { type, has: true, statuses: ["trialing", "active", "past_due"] };
    case "access":
      return { type, has: true, access_keys: ["club"] };
    case "field":
      return { type, field: "first_name", op: "eq", value: "" };
    case "date":
      return { type, field: "created_at", op: "within_last_days", days: 30 };
    case "utm":
      return { type, touch: "any", source: "" };
    case "email_status":
      return { type, statuses: ["ok"] };
    case "affiliate":
      return { type, has: true, affiliate_ids: [] };
    case "segment":
      return { type };
    case "contact_ids":
      return { type, contact_ids: [] };
    case "group":
      return { type, match: "any", rules: [] };
  }
}

const ids = (s: string) =>
  s
    .split(/[\s,]+/)
    .map((x) => Number(x))
    .filter((n) => Number.isInteger(n) && n > 0);

/** One rule with an editor that fits its type. */
export function RuleEditor({
  rule,
  onChange,
  onRemove,
  depth = 0,
  segments,
}: {
  rule: SegmentRule;
  onChange: (r: SegmentRule) => void;
  onRemove: () => void;
  depth?: number;
  segments: { id: number; name: string }[];
}) {
  const set = (patch: Partial<SegmentRule>) => onChange({ ...rule, ...patch });
  const type = RULE_TYPES.find((t) => t.value === rule.type);
  return (
    <div className="rounded-md border bg-[var(--color-card)] p-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <Select className="w-auto" aria-label="Rule type" value={rule.type} onChange={(e) => onChange(newRule(e.target.value as SegmentRule["type"]))}>
          {RULE_TYPES.filter((t) => depth < 1 || t.value !== "group").map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </Select>
        <label className="flex items-center gap-1.5 text-xs text-[var(--color-muted-foreground)]" title="Turns the rule around: people who match it are left out">
          <Checkbox checked={!!rule.not} onChange={(e) => set({ not: e.target.checked || undefined })} /> leave these people out
        </label>
        <button className="ml-auto rounded p-1 text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)] hover:text-[var(--color-destructive)]" aria-label="Remove rule" onClick={onRemove}>
          <Trash2 className="size-4" />
        </button>
      </div>
      <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">{type?.help}</p>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
        <Body rule={rule} set={set} depth={depth} segments={segments} />
      </div>
    </div>
  );
}

function Body({ rule, set, depth, segments }: { rule: SegmentRule; set: (p: Partial<SegmentRule>) => void; depth: number; segments: { id: number; name: string }[] }) {
  const fields = useCustomFields();
  const custom = fields.data?.fields ?? [];
  const dateRange = (a: "applied_since" | "since", b: "applied_before" | "before") => (
    <>
      <span className="text-xs text-[var(--color-muted-foreground)]">from</span>
      <Input type="date" className="h-8 w-36" value={(rule[a] as string) ?? ""} onChange={(e) => set({ [a]: e.target.value || undefined })} aria-label="From date" />
      <span className="text-xs text-[var(--color-muted-foreground)]">before</span>
      <Input type="date" className="h-8 w-36" value={(rule[b] as string) ?? ""} onChange={(e) => set({ [b]: e.target.value || undefined })} aria-label="Before date" />
    </>
  );
  const has = (yes: string, no: string) => (
    <Select className="w-auto" value={rule.has === false ? "no" : "yes"} onChange={(e) => set({ has: e.target.value === "yes" })} aria-label="Has or not">
      <option value="yes">{yes}</option>
      <option value="no">{no}</option>
    </Select>
  );
  switch (rule.type) {
    case "tag":
      return (
        <>
          <Select className="w-auto" value={rule.tag_match ?? "any"} onChange={(e) => set({ tag_match: e.target.value as SegmentRule["tag_match"] })} aria-label="Which tags">
            <option value="any">any of</option>
            <option value="all">all of</option>
            <option value="none">none of</option>
          </Select>
          <TagChips ids={rule.tag_ids ?? []} onChange={(tag_ids) => set({ tag_ids })} />
          {dateRange("applied_since", "applied_before")}
        </>
      );
    case "tag_event":
      return (
        <>
          <TagChips ids={rule.tag_ids ?? []} onChange={(tag_ids) => set({ tag_ids })} />
          <Select className="w-auto" value={rule.action ?? "applied"} onChange={(e) => set({ action: e.target.value as "applied" | "removed" })} aria-label="Added or removed">
            <option value="applied">was added</option>
            <option value="removed">was removed</option>
          </Select>
          {dateRange("since", "before")}
        </>
      );
    case "brand":
      return (
        <>
          {BRANDS.map((b) => (
            <label key={b.id} className="flex items-center gap-1 text-sm">
              <Checkbox
                checked={(rule.brand_ids ?? []).includes(b.id)}
                onChange={(e) => set({ brand_ids: e.target.checked ? [...(rule.brand_ids ?? []), b.id] : (rule.brand_ids ?? []).filter((x) => x !== b.id) })}
              />
              {b.short}
            </label>
          ))}
          <Select className="w-auto" value={rule.marketing_status ?? ""} onChange={(e) => set({ marketing_status: (e.target.value || undefined) as SegmentRule["marketing_status"] })} aria-label="Subscription">
            <option value="">any email status</option>
            <option value="subscribed">subscribed to its email</option>
            <option value="unsubscribed">unsubscribed</option>
            <option value="none">never subscribed</option>
          </Select>
        </>
      );
    case "engagement":
      return (
        <>
          {has("did", "did NOT")}
          <Select className="w-auto" value={rule.event ?? "any"} onChange={(e) => set({ event: e.target.value })} aria-label="What">
            {ENGAGEMENT_EVENTS.map((e) => (
              <option key={e.value} value={e.value}>
                {e.label}
              </option>
            ))}
          </Select>
          <span className="text-xs text-[var(--color-muted-foreground)]">in the last</span>
          <Input type="number" min={1} className="h-8 w-20" value={rule.within_days ?? 90} onChange={(e) => set({ within_days: Number(e.target.value) || 1 })} aria-label="Days" />
          <span className="text-xs text-[var(--color-muted-foreground)]">days</span>
          {(rule.event === "email_open" || rule.event === "email" || rule.event === "any") && (
            <label className="flex items-center gap-1 text-xs text-[var(--color-muted-foreground)]">
              <Checkbox checked={!!rule.include_machine_opens} onChange={(e) => set({ include_machine_opens: e.target.checked || undefined })} /> count machine opens too
            </label>
          )}
        </>
      );
    case "email_activity":
      return (
        <>
          <span className="text-xs text-[var(--color-muted-foreground)]">email report #</span>
          <Input inputMode="numeric" className="h-8 w-24" value={rule.report_unit_id ?? ""} onChange={(e) => set({ report_unit_id: Number(e.target.value.replace(/\D/g, "")) || undefined })} aria-label="Report ID" />
          <Select className="w-auto" value={rule.activity ?? "clicked"} onChange={(e) => set({ activity: e.target.value })} aria-label="What they did">
            {ACTIVITIES.map((a) => (
              <option key={a.value} value={a.value}>
                {a.label}
              </option>
            ))}
          </Select>
        </>
      );
    case "purchase":
      return (
        <>
          {has("bought", "never bought")}
          <Input className="h-8 w-40" placeholder="product IDs (any)" value={(rule.product_ids ?? []).join(", ")} onChange={(e) => set({ product_ids: ids(e.target.value) })} aria-label="Product IDs" />
          <Select className="w-auto" value={rule.brand_id ?? ""} onChange={(e) => set({ brand_id: Number(e.target.value) || undefined })} aria-label="From brand">
            <option value="">from any brand</option>
            {BRANDS.map((b) => (
              <option key={b.id} value={b.id}>
                from {b.short}
              </option>
            ))}
          </Select>
          <Input type="number" min={0} className="h-8 w-28" placeholder="min $" value={rule.min_total_cents ? rule.min_total_cents / 100 : ""} onChange={(e) => set({ min_total_cents: e.target.value ? Math.round(Number(e.target.value) * 100) : undefined })} aria-label="Minimum total in dollars" />
          {dateRange("since", "before")}
        </>
      );
    case "subscription":
      return (
        <>
          {has("has a subscription that is", "has no subscription that is")}
          {["trialing", "active", "past_due", "cancelled"].map((st) => (
            <label key={st} className="flex items-center gap-1 text-sm">
              <Checkbox checked={(rule.statuses ?? []).includes(st)} onChange={(e) => set({ statuses: e.target.checked ? [...(rule.statuses ?? []), st] : (rule.statuses ?? []).filter((x) => x !== st) })} />
              {st.replace("_", " ")}
            </label>
          ))}
        </>
      );
    case "access":
      return (
        <>
          {has("has access to", "doesn't have access to")}
          <Input className="h-8 w-56" value={(rule.access_keys ?? []).join(", ")} onChange={(e) => set({ access_keys: e.target.value.split(/[\s,]+/).filter(Boolean) })} placeholder="club, manager_training" aria-label="Access keys" />
        </>
      );
    case "field": {
      const op = TEXT_OPS.find((o) => o.value === rule.op);
      return (
        <>
          <Select className="w-auto" value={rule.field ?? "first_name"} onChange={(e) => set({ field: e.target.value })} aria-label="Field">
            {TEXT_FIELDS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
            {custom.length > 0 && (
              <optgroup label="Custom fields">
                {custom
                  .filter((f) => !["date", "datetime"].includes(f.type))
                  .map((f) => (
                    <option key={f.key} value={`custom.${f.key}`}>
                      {f.label}
                    </option>
                  ))}
              </optgroup>
            )}
          </Select>
          <Select className="w-auto" value={rule.op ?? "eq"} onChange={(e) => set({ op: e.target.value })} aria-label="Comparison">
            {TEXT_OPS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
          {!op?.noValue && <Input className="h-8 w-56" value={String(rule.value ?? "")} onChange={(e) => set({ value: e.target.value })} aria-label="Value" />}
        </>
      );
    }
    case "date": {
      const op = DATE_OPS.find((o) => o.value === rule.op);
      return (
        <>
          <Select className="w-auto" value={rule.field ?? "created_at"} onChange={(e) => set({ field: e.target.value })} aria-label="Date">
            {DATE_FIELDS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
            {custom.some((f) => f.type === "date" || f.type === "datetime") && (
              <optgroup label="Custom fields">
                {custom
                  .filter((f) => f.type === "date" || f.type === "datetime")
                  .map((f) => (
                    <option key={f.key} value={`custom.${f.key}`}>
                      {f.label}
                    </option>
                  ))}
              </optgroup>
            )}
          </Select>
          <Select className="w-auto" value={rule.op ?? "after"} onChange={(e) => set({ op: e.target.value, value: undefined, value2: undefined, days: undefined })} aria-label="Comparison">
            {DATE_OPS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
          {(op?.needs === "date" || op?.needs === "two") && <Input type="date" className="h-8 w-36" value={String(rule.value ?? "")} onChange={(e) => set({ value: e.target.value })} aria-label="Date" />}
          {op?.needs === "two" && (
            <>
              <span className="text-xs text-[var(--color-muted-foreground)]">and</span>
              <Input type="date" className="h-8 w-36" value={String(rule.value2 ?? "")} onChange={(e) => set({ value2: e.target.value })} aria-label="Second date" />
            </>
          )}
          {op?.needs === "days" && <Input type="number" min={1} className="h-8 w-20" value={rule.days ?? 30} onChange={(e) => set({ days: Number(e.target.value) || 1 })} aria-label="Days" />}
          {op?.needs === "month" && <Input type="number" min={1} max={12} className="h-8 w-20" value={String(rule.value ?? "")} onChange={(e) => set({ value: e.target.value })} aria-label="Month number" />}
        </>
      );
    }
    case "utm":
      return (
        <>
          <Select className="w-auto" value={rule.touch ?? "any"} onChange={(e) => set({ touch: e.target.value as SegmentRule["touch"] })} aria-label="First or last touch">
            <option value="any">first or last touch</option>
            <option value="first">first touch</option>
            <option value="last">last touch</option>
          </Select>
          {(["source", "medium", "campaign"] as const).map((k) => (
            <Input key={k} className="h-8 w-36" placeholder={k} value={rule[k] ?? ""} onChange={(e) => set({ [k]: e.target.value || undefined })} aria-label={`UTM ${k}`} />
          ))}
        </>
      );
    case "email_status":
      return (
        <>
          {EMAIL_STATUSES.map((st) => (
            <label key={st} className="flex items-center gap-1 text-sm">
              <Checkbox checked={(rule.statuses ?? []).includes(st)} onChange={(e) => set({ statuses: e.target.checked ? [...(rule.statuses ?? []), st] : (rule.statuses ?? []).filter((x) => x !== st) })} />
              {st === "ok" ? "mailable" : st.replace("_", " ")}
            </label>
          ))}
        </>
      );
    case "affiliate":
      return (
        <>
          {has("referred by", "not referred by")}
          <Input className="h-8 w-48" placeholder="affiliate IDs (any)" value={(rule.affiliate_ids ?? []).join(", ")} onChange={(e) => set({ affiliate_ids: ids(e.target.value) })} aria-label="Affiliate IDs" />
        </>
      );
    case "segment":
      return (
        <Select className="w-auto" value={rule.segment_id ?? ""} onChange={(e) => set({ segment_id: Number(e.target.value) || undefined })} aria-label="Segment">
          <option value="">Pick a segment…</option>
          {segments.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
      );
    case "contact_ids":
      return <Input className="h-8 w-full" placeholder="Contact IDs, separated by commas" value={(rule.contact_ids ?? []).join(", ")} onChange={(e) => set({ contact_ids: ids(e.target.value) })} aria-label="Contact IDs" />;
    case "group":
      return (
        <div className="w-full space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-xs text-[var(--color-muted-foreground)]">matches</span>
            <Select className="w-auto" value={rule.match ?? "any"} onChange={(e) => set({ match: e.target.value as "any" | "all" })} aria-label="Group match">
              <option value="any">any</option>
              <option value="all">all</option>
            </Select>
            <span className="text-xs text-[var(--color-muted-foreground)]">of these:</span>
          </div>
          <RuleList rules={rule.rules ?? []} onChange={(rules) => set({ rules })} depth={depth + 1} segments={segments} />
        </div>
      );
  }
}

export function RuleList({ rules, onChange, depth = 0, segments }: { rules: SegmentRule[]; onChange: (r: SegmentRule[]) => void; depth?: number; segments: { id: number; name: string }[] }) {
  const [adding, setAdding] = useState<SegmentRule["type"]>("tag");
  return (
    <div className="space-y-2">
      {rules.map((r, i) => (
        <RuleEditor
          key={i}
          rule={r}
          depth={depth}
          segments={segments}
          onChange={(nr) => onChange(rules.map((x, j) => (j === i ? nr : x)))}
          onRemove={() => onChange(rules.filter((_, j) => j !== i))}
        />
      ))}
      <div className="flex items-center gap-2">
        <Select className="w-auto" value={adding} onChange={(e) => setAdding(e.target.value as SegmentRule["type"])} aria-label="Kind of rule to add">
          {RULE_TYPES.filter((t) => depth < 1 || t.value !== "group").map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </Select>
        <Button size="sm" variant="outline" onClick={() => onChange([...rules, newRule(adding)])}>
          <Plus /> Add rule
        </Button>
      </div>
    </div>
  );
}

export function TagChips({ ids: tagIds, onChange }: { ids: number[]; onChange: (ids: number[]) => void }) {
  const tags = useTags(true);
  const [open, setOpen] = useState(false);
  const name = (id: number) => tags.data?.tags.find((t) => t.id === id)?.name ?? `#${id}`;
  return (
    <span className="flex flex-wrap items-center gap-1">
      {tagIds.map((id) => (
        <Badge key={id} variant="default">
          {name(id)}
          <button aria-label={`Remove ${name(id)}`} onClick={() => onChange(tagIds.filter((x) => x !== id))}>
            <X className="size-3" />
          </button>
        </Badge>
      ))}
      <button className="inline-flex items-center gap-1 rounded-full border border-dashed px-2 py-0.5 text-xs text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]" onClick={() => setOpen(true)}>
        <Plus className="size-3" /> tag
      </button>
      <TagPicker title="Pick a tag" exclude={new Set(tagIds)} open={open} onOpenChange={setOpen} onPick={(id) => onChange([...tagIds, id])} />
    </span>
  );
}
